import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  LinearProgress,
  IconButton
} from "@mui/material";
import { IoClose } from "react-icons/io5";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { toast } from "react-toastify";
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { incrementUploadCounter } from "../../utils/uploadCounter";
import { authenticatedFetch } from "../../utils/authApi";

// Backend API URL - use production URL from env or fallback
const API_BASE_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:5001';

/**
 * Call backend API to extract policy data from PDF
 * @param {File} pdfFile - PDF file to process
 * @returns {Promise<Object>} - Extracted policy data
 */
const extractPolicyDataFromPDF = async (pdfFile) => {
  const formData = new FormData();
  formData.append('pdf', pdfFile);

  // Use authenticatedFetch to include Firebase ID token
  const response = await authenticatedFetch(`${API_BASE_URL}/api/pdf/extract`, {
    method: 'POST',
    body: formData,
    // Note: Don't set Content-Type header for FormData - browser sets it automatically with boundary
  });

  if (!response.ok) {
    if (response.status === 429) {
      throw new Error('You have exceeded the upload limit. Please try again later.');
    }
    const errorData = await response.json().catch(() => ({}));
    console.error('Backend extraction error:', errorData);
    throw new Error(errorData.message || 'PDF extraction failed');
  }

  const result = await response.json();
  return result.data;
};

const UploadPolicyModal = ({
  isOpen,
  onClose,
  onExtracted,
  setSelectedFile,
  _setShowSuccessDialog,
  setPdfUrl,
  user
}) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [abortController, setAbortController] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const processFile = (selected) => {
    if (
      selected &&
      selected.type === "application/pdf" &&
      selected.name.toLowerCase().endsWith(".pdf")
    ) {
      setFile(selected);
      setSelectedFile && setSelectedFile(selected);
    } else {
      toast.error("Please upload a valid PDF file.");
    }
  };

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    processFile(selected);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles && droppedFiles.length > 0) {
      processFile(droppedFiles[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    // Create new AbortController for this upload session
    const controller = new AbortController();
    setAbortController(controller);
    setUploading(true);
    setProgress(0);

    try {
      // Check if cancelled before starting extraction
      if (controller.signal.aborted) {
        return;
      }

      // Call backend API to extract policy data
      const parsed = await extractPolicyDataFromPDF(file);

      console.log("✅ Policy data extracted from backend:", parsed);

      // Check if cancelled after extraction
      if (controller.signal.aborted) {
        return;
      }

      // Validate extracted data (relaxed validation - allow partial data)
      if (!parsed) {
        console.error("❌ No data extracted from PDF");
        toast.error("We couldn't extract data from this PDF. Please check the format or upload another file.", {
          containerId: "bottom-center",
          position: "bottom-center",
          autoClose: 3000,
          hideProgressBar: true,
          closeOnClick: false,
          pauseOnHover: false,
          draggable: false,
          closeButton: false,
          style: {
            backgroundColor: '#FFADA8',
            color: '#FFFFFF',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: 500,
            padding: '12px 16px',
            minHeight: '44px',
            alignItems: 'center',
            gap: '2px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            marginBottom: '75vh',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            width: 'auto',
            maxWidth: 'none',
            display: 'inline-flex',
          },
          icon: (
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ color: '#FFFFFF' }}
            >
              <path
                d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ),
        });
        setUploading(false);
        return;
      }

      // Log extracted data for debugging
      console.log("✅ Policy data extracted from backend:", parsed);
      console.log("📋 Extracted fields:", {
        hasCustomer: !!parsed.customer,
        hasPolicy: !!parsed.policy,
        customerName: parsed.customer?.name,
        policyNumber: parsed.policy?.policyNumber,
        hasVehicle: !!parsed.vehicle,
        hasPayment: !!parsed.payment
      });

      // Increment the upload counter for this user
      if (user?.uid) {
        try {
          console.log('🔄 Attempting to increment upload counter for user:', user.uid);
          await incrementUploadCounter(
            user.uid,
            user.displayName || 'Unknown User',
            user.email || '',
            user
          );
          console.log('✅ Successfully incremented upload counter');
        } catch (error) {
          console.error('❌ Error incrementing upload counter:', error);
          // Don't block the upload flow if counter fails
        }
      } else {
        console.warn('⚠️ Cannot increment counter: No user UID available');
      }

      // Allow form to open even with partial data - user can fill in missing fields
      // Only block if we have absolutely no useful data

      // Check if cancelled before finishing
      if (controller.signal.aborted) {
        return;
      }

      // Pass the extracted data and the raw file to the parent
      onExtracted?.({ ...parsed, rawFile: file, ready: true });
      setSelectedFile?.(file);

      toast.success("Policy successfully extracted", {
        containerId: "bottom-center",
        position: "bottom-center",
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: false,
        draggable: false,
        closeButton: false,
        style: {
          backgroundColor: '#DCFCE7',
          color: '#FFFFFF',
          borderRadius: '8px',
          fontSize: '16px',
          fontWeight: 500,
          padding: '12px 16px',
          minHeight: '44px',
          alignItems: 'center',
          gap: '2px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          marginBottom: '75vh',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: 'auto',
          maxWidth: 'none',
          display: 'inline-flex',
        },
        icon: (
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ color: '#FFFFFF' }}
          >
            <path
              d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ),
      });

      // Close the modal
      onClose?.();
      setUploading(false);
      setProgress(0);
      setAbortController(null); // Clean up abort controller

    } catch (error) {
      console.error("❌ General extraction error:", error);
      toast.error("We couldn't extract data from this PDF. Please check the format or upload another file.", {
        containerId: "bottom-center",
        position: "bottom-center",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: false,
        draggable: false,
        closeButton: false,
        style: {
          backgroundColor: '#FFADA8',
          color: '#FFFFFF',
          borderRadius: '8px',
          fontSize: '16px',
          fontWeight: 500,
          padding: '12px 16px',
          minHeight: '44px',
          alignItems: 'center',
          gap: '2px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          marginBottom: '75vh',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: 'auto',
          maxWidth: 'none',
          display: 'inline-flex',
        },
        icon: (
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ color: '#FFFFFF' }}
          >
            <path
              d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ),
      });
      setUploading(false);
      setProgress(0);
      setAbortController(null); // Clean up abort controller
    }
  };

  // Custom close handler that cancels ongoing operations
  const handleClose = () => {
    if (abortController) {
      console.log("🚫 Cancelling ongoing extraction...");
      abortController.abort();
      setAbortController(null);
    }

    // Reset states
    setUploading(false);
    setProgress(0);
    setFile(null);

    // Clear any cached file data
    setSelectedFile && setSelectedFile(null);
    setPdfUrl && setPdfUrl(null);

    // Call the original onClose
    onClose();
  };

  // Clear cache when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setFile(null);
      setUploading(false);
      setProgress(0);
      setAbortController(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2, textAlign: "left !important", position: 'relative' }}>
        <Typography
          className="upload-policy-header"
          sx={{
            fontFamily: "Roboto Slab !important",
            fontWeight: "500 !important",
            fontStyle: "Medium",
            fontSize: "32px !important",
            lineHeight: "56px !important",
            letterSpacing: "2% !important",
            color: "#000000 !important",
            textAlign: "left !important"
          }}
        >
          Upload Policy PDF
        </Typography>
        <IconButton
          aria-label="Close"
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <IoClose size={22} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ textAlign: "center" }}>
        <Box
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            border: isDragging ? "2px dashed #0063F7" : "2px dashed #ccc",
            borderRadius: "4px",
            p: 4,
            mb: 2,
            backgroundColor: isDragging ? "#F0F7FF" : "#FAFAFA",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 180,
            transition: "all 0.2s ease-in-out"
          }}
        >
          <PictureAsPdfIcon sx={{ fontSize: 40, color: "#757575", mb: 1 }} />
          <Typography
            component="div"
            sx={{
              fontFamily: "Roboto",
              fontWeight: 400,
              fontSize: "22px",
              lineHeight: "30px",
              mb: 2,
              maxWidth: "400px",
              margin: "0 auto",
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center'
            }}
          >
            <div>Drag and drop or click to upload. We'll</div>
            <div>extract the data for you</div>
          </Typography>

          <Button
            component="label"
            sx={{
              width: "112px",
              height: "24px",
              borderRadius: "6px",
              fontFamily: "Roboto",
              fontWeight: 400,
              fontSize: "20px",
              lineHeight: "24px",
              letterSpacing: "0.01em",
              color: "#0063F7",
              whiteSpace: "nowrap",
              textTransform: "none",
              gap: "8px",
              mt: 2,
              "&:hover": {
                textDecoration: "underline",
                fontWeight: 500,
                backgroundColor: "inherit"
              }
            }}
          >
            Choose files
            <input
              hidden
              type="file"
              accept="application/pdf"
              onChange={handleFileChange}
            />
          </Button>
        </Box>

        {file && (
          <Typography
            sx={{ fontSize: "14px", color: "#666", mt: 1, textAlign: "center" }}
          >
            Selected: {file.name}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: "flex-end", gap: 2, py: 2, pr: 3 }}>
        {!uploading ? (
          <>
            <Button
              onClick={handleClose}
              variant="outlined"
              sx={{
                width: "149px",
                height: "60px",
                borderRadius: "6px",
                borderColor: "#B00020",
                color: "#B00020",
                fontFamily: "Roboto",
                fontWeight: 400,
                fontSize: "20px",
                lineHeight: "24px",
                letterSpacing: "0.01em",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "inherit",
                  color: "#B00020",
                  boxShadow: 10
                }
              }}
            >
              Cancel
            </Button>

            <Button
              onClick={handleUpload}
              disabled={!file || uploading}
              variant="contained"
              sx={{
                width: "149px",
                height: "60px",
                borderRadius: "6px",
                backgroundColor: '#0063F7 !important',
                fontFamily: "Roboto",
                fontWeight: 400,
                fontSize: "20px",
                lineHeight: "24px",
                letterSpacing: "0.01em",
                textTransform: "none",
                "&:disabled": {
                  backgroundColor: "#CCCCCC !important",
                  color: "#666666 !important",
                },
                '&:hover': {
                  backgroundColor: '#0063F7 !important',
                  color: '#FFFFFF !important',
                  boxShadow: 10
                }
              }}
            >
              {uploading ? "Extracting..." : "Submit"}
            </Button>
          </>
        ) : (
          <Box
            sx={{
              width: 536,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1
            }}
          >
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                width: "536px",
                height: "9px",
                borderRadius: "12px",
                backgroundColor: "#E5E7EB",
                "& .MuiLinearProgress-bar": {
                  backgroundColor: "#0063F7"
                }
              }}
            />
            <Typography
              sx={{
                fontFamily: "Roboto",
                fontWeight: 400,
                fontSize: "16px",
                lineHeight: "24px",
                color: "#000000"
              }}
            >
              {progress}%
            </Typography>
          </Box>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default UploadPolicyModal;
